import { useMount } from 'ahooks';
import { useAppDispatch } from 'app/hooks';
import { RevxSkeleton } from 'components';
import { RevxButton } from 'components/RevxButton';
import { FileItem, JobPoView } from 'domains/workspaceDetailDomain';
import {
  JobItem,
  JobTypeEnum,
  JobWorkflowStatusEnum,
} from 'domains/workspaceDomain';
import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import jobService from 'services/jobService';
import { newFileObject } from 'utils/misc';
import {
  CommonBillingInfo,
  CommonJobAdjustmentLink,
  CommonSummary,
  CommonSummaryTotal,
  CommonTotalJobCost,
  CommonUploadFile,
} from '../../common';
import { sleep } from 'utils/misc';
import { WORKSPACE_DELAY_TIME } from 'utils/constants';
import './SupplierBilling.less';

const SupplierBilling = (jobData: JobItem) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [poView, setPoView] = useState<Partial<JobPoView>>({});
  const [loading, setLoading] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [defaultFileList, setDefaultFileList] = useState<FileItem[]>();

  const statusFilters = useJobStatusCollection();

  useMount(async () => {
    setLoading(true);
    const poRes = await dispatch.workspaceDetail.poView(jobData);
    setPoView(poRes);
    if (poRes.jobSummary) {
      const {
        invoice: { supplierInvoiceReferenceFiles },
      } = poRes.jobSummary;
      const otherFiles =
        supplierInvoiceReferenceFiles?.map((i) => {
          return newFileObject(i.name, i.path, jobData.id);
        }) || [];
      setDefaultFileList(otherFiles);
    }
    setLoading(false);
  });
  return (
    <div className="supplier-billing-card">
      <RevxSkeleton loading={loading}>
        {jobData.workflowStatus === JobWorkflowStatusEnum.INVOICING && (
          <div>
            <div className="supplier-billing-card-header">
              <hgroup>
                <span>{t('workspace.detail.po.supplierInvoice')}</span>
              </hgroup>
            </div>
            <div className="common-billing-card-header-container invoice-upload-container">
              <CommonUploadFile
                showUploadButton={true}
                defaultFileList={defaultFileList}
                itemData={jobData}
                deleteFileHandler={async (fileItem: FileItem) => {
                  try {
                    await dispatch.app.showLoading();
                    await jobService.deleteInvoiceFile(fileItem.jobId, {
                      path: fileItem.path,
                    });
                    return 'success';
                  } catch {
                    return '';
                  } finally {
                    await dispatch.app.hideLoading();
                  }
                }}
                getAllFileHandler={dispatch.workspaceDetail.getAllFile}
                saveFileHandler={async (fileItem: FileItem) => {
                  const { fileName, path } = fileItem;
                  await jobService.saveInvoiceFile(fileItem.id, {
                    name: fileName,
                    path,
                  });
                  const file = newFileObject(fileName, path, jobData.id);
                  return Promise.resolve(file);
                }}
                uploadIconBeforeOldFiles={false}
              />
            </div>
          </div>
        )}
        <div className="supplier-billing-card-header">
          <hgroup>
            <span>{t('workspace.detail.po.billing')}</span>
          </hgroup>
        </div>
        <CommonBillingInfo {...poView} />
        <CommonSummary
          {...poView.jobSummary}
          printingTotal={poView.jobSummary?.supplierPrintingCharge}
          shippingTotal={poView.jobSummary?.supplierFreightCharge}
        />

        {jobData.jobType === JobTypeEnum.INTERNAL ? (
          <CommonSummaryTotal
            total={poView.jobSummary?.supplierCharge}
            // markupCharge={poView.jobSummary?.outsourceMarkup}
          />
        ) : (
          <CommonTotalJobCost totalCharge={poView.jobSummary?.supplierCharge} />
        )}
        <CommonJobAdjustmentLink jobData={jobData} />
        <div className="action">
          {jobData.workflowStatus === JobWorkflowStatusEnum.INVOICING && (
            <RevxButton
              loading={markingPaid}
              onClick={async () => {
                await dispatch.app.showLoading();
                setMarkingPaid(true);
                try {
                  await jobService.supplierMarkAsPaid(jobData.id);
                } catch {}
                setMarkingPaid(false);
                dispatch.alert.getNotificationAndAlerts();
                await sleep(WORKSPACE_DELAY_TIME);
                await dispatch.app.hideLoading();
                dispatch.workspace.reloadJobList();
                dispatch.workspace.getJobCountByStatusFilter(statusFilters);
              }}
            >
              {t('workspace.detail.supplierBilling.markAsPaid')}
            </RevxButton>
          )}
        </div>
      </RevxSkeleton>
    </div>
  );
};

export default SupplierBilling;
